import type { AdminRecord } from '@/types/record'

export { SHANGHAI_TIME_ZONE, formatDate, formatDateTime, nowIso } from '@/foundation/time'
import { formatDate, formatDateTime, nowIso } from '@/foundation/time'

export function isoDaysAgo(days: number, hour = 9, minute = 0): string {
  const base = new Date('2026-10-17T12:00:00+08:00')
  const date = new Date(base.getTime() - days * 86_400_000)
  const local = new Date(date.getTime() + 8 * 3_600_000)
  const pad = (value: number): string => String(value).padStart(2, '0')
  return `${local.getUTCFullYear()}-${pad(local.getUTCMonth() + 1)}-${pad(local.getUTCDate())}T${pad(hour)}:${pad(minute)}:00+08:00`
}

export function isoHoursAgo(hours: number, minute = 0): string {
  const base = new Date('2026-10-17T12:00:00+08:00')
  const date = new Date(base.getTime() - hours * 3_600_000)
  const local = new Date(date.getTime() + 8 * 3_600_000)
  const pad = (value: number): string => String(value).padStart(2, '0')
  return `${local.getUTCFullYear()}-${pad(local.getUTCMonth() + 1)}-${pad(local.getUTCDate())}T${pad(local.getUTCHours())}:${pad(minute)}:00+08:00`
}

export function formatMoney(fen: unknown): string {
  const value = typeof fen === 'number' ? fen : Number(fen)
  if (!Number.isFinite(value)) return '¥0.00'
  return `¥${(value / 100).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export function formatNumber(value: unknown): string {
  const number = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(number)) return '0'
  return number.toLocaleString('zh-CN')
}

export function formatPercent(value: unknown, precision = 1): string {
  const number = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(number)) return '0%'
  return `${number.toFixed(precision)}%`
}

export function formatPhone(value: unknown): string {
  const text = String(value ?? '').replace(/\s+/g, '')
  if (!text) return '—'
  if (text.includes('*')) return text
  if (/^1\d{10}$/.test(text)) return `${text.slice(0, 3)}****${text.slice(7)}`
  return text
}

export function maskAddress(value: unknown): string {
  const text = String(value ?? '')
  if (!text) return '—'
  const match = /^(.{0,6}).*(室|号|栋|弄|路|区)$/.exec(text)
  return match ? `${match[1]}****${match[2]}` : text.length > 12 ? `${text.slice(0, 10)}****` : text
}

export function formatBoolean(value: unknown): string {
  if (value === true || value === 'true' || value === 1) return '是'
  if (value === false || value === 'false' || value === 0) return '否'
  return '—'
}

const SUCCESS_WORDS = ['正常', '已上架', '已处理', '已完成', '已发送', '在线', '已通过', '启用', '生效中', '已解决', '已回复']
const INFO_WORDS = ['处理中', '待提醒', '生成中', '同步中', '已受理', '进行中', '已排期']
const WARNING_WORDS = ['待审核', '待处理', '库存低', '待受理', '未激活', '待认证', '待跟进', '待回复', '待发送', '草稿']
const DANGER_WORDS = ['高风险', '已逾期', '停用', '已停用', '故障', '已驳回', '退款', '退款中', '已失败', '紧急']

export function statusTone(label: string): string {
  if (SUCCESS_WORDS.some(word => label.includes(word))) return 'success'
  if (INFO_WORDS.some(word => label.includes(word))) return 'info'
  if (WARNING_WORDS.some(word => label.includes(word))) return 'warning'
  if (DANGER_WORDS.some(word => label.includes(word))) return 'danger'
  return 'neutral'
}

const ISO_PATTERN = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?$/
const SNAKE_CASE = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/

export function safeText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (Array.isArray(value)) {
    const items = value.map(item => safeText(item)).filter(item => item !== '—')
    return items.length ? items.join('、') : '—'
  }
  if (typeof value === 'object') return '—'
  const text = String(value)
  if (ISO_PATTERN.test(text)) return formatDateTime(text)
  if (SNAKE_CASE.test(text)) return '—'
  return text
}

export interface CellFormatContext {
  statusMap?: Record<string, { label: string; tone?: string }>
  columnType?: string
  suffix?: string
  options?: Array<{ value: string; label: string }>
}

export function formatCellValue(value: unknown, row: AdminRecord = {}, context: CellFormatContext = {}): string {
  const { columnType, statusMap, suffix, options } = context
  const optionLabel = options?.find(option => option.value === String(value ?? ''))?.label
  if (optionLabel) return optionLabel
  switch (columnType) {
    case 'money':
      return formatMoney(value)
    case 'number':
      return suffix ? `${formatNumber(value)}${suffix}` : formatNumber(value)
    case 'percent':
      return formatPercent(value)
    case 'datetime':
      return formatDateTime(value)
    case 'date':
      return formatDate(value)
    case 'phone':
      return formatPhone(value)
    case 'boolean':
      return formatBoolean(value)
    case 'progress': {
      const numeric = Number(value)
      return Number.isFinite(numeric) ? `${Math.round(numeric)}%` : '—'
    }
    case 'status': {
      const key = String(value ?? '')
      return statusMap?.[key]?.label ?? safeText(value)
    }
    case 'tag':
      return safeText(value)
    case 'user':
    case 'region':
    case 'link':
    case 'text':
    case 'primary':
    case 'readonly':
    default:
      if (typeof value === 'string' && ISO_PATTERN.test(value)) return formatDateTime(value)
      if (value === null || value === undefined) {
        for (const key of ['name', 'title', 'label']) {
          const fallback = row[key]
          if (typeof fallback === 'string' && fallback) return safeText(fallback)
        }
      }
      return safeText(value)
  }
}

export function displayName(row: AdminRecord, keys: string[] = ['name', 'title', 'label']): string {
  for (const key of keys) {
    const value = row[key]
    if (typeof value === 'string' && value) return safeText(value)
  }
  return '—'
}

export function excerpt(value: unknown, length = 38): string {
  const text = safeText(value)
  return text.length > length ? `${text.slice(0, length)}…` : text
}

export function dateOnlyShanghai(value: unknown): string {
  return formatDate(value)
}

export function monthKey(value: unknown): string {
  return formatDate(value).slice(0, 7)
}
